<template>
  <Head :title="t('roles')" />
  <s-page-block>
    <s-page-header :heading="t('roles')" class="mb-3"></s-page-header>
    <s-filter-group class="mb-4">
      <s-multi-select-filter
        v-model="filters.permissions"
        :label="t('permissions')"
        :options="
          permissions.map((permission: string) => ({
            label: t(`permissions.${permission}`),
            value: permission,
          }))
        "
      />
      <s-multi-select-filter
        v-model="filters.roles"
        :label="t('roles')"
        :options="
          roles.map((role) => ({
            label: role.name,
            value: role.id,
          }))
        "
      />
      <s-select-filter
        v-model="filters.scope"
        :label="t('scope')"
        :options="
          Object.keys(scopeToRoleMappings).map((scope) => ({
            label: scope,
            value: scope,
          }))
        "
      />
    </s-filter-group>

    <s-table density="denser">
      <template #header>
        <tr>
          <th>{{ t('role') }}</th>
          <th>{{ t('scope') }}</th>
        </tr>
      </template>
      <template #body>
        <tr v-for="role in filteredRoles" :key="role.id">
          <td>{{ role.name }}</td>
          <td>{{ getScopeForRole(role.id) }}</td>
        </tr>
      </template>
    </s-table>

    <div class="mt-6">
      <h2 class="heading secondary mb-3">{{ t('permissions') }}</h2>
      <s-table
        density="denser"
        class="sticky-first-column zebra-columns max-h-[85vh] overflow-y-auto"
      >
        <template #header>
          <tr>
            <th>{{ t('permissions') }}</th>
            <th v-for="role in filteredRoles" :key="role.id">{{ role.name }}</th>
          </tr>
        </template>
        <template #body>
          <tr v-for="permission in filteredPermissions" :key="permission">
            <td class="whitespace-nowrap">
              {{ t(`permissions.${permission}`) }}
            </td>
            <td
              v-for="role in filteredRoles"
              :key="role.id"
              :class="{
                'green-cell !p-0 text-center': role.permissions.includes(permission),
              }"
            >
              <span class="flex items-center justify-center">
                <s-icon
                  v-if="role.permissions.includes(permission)"
                  name="check"
                  class="text-green-700"
                />
              </span>
            </td>
          </tr>
        </template>
      </s-table>
    </div>
  </s-page-block>
</template>
<script setup lang="ts">
import {Head} from '@inertiajs/vue3';
import {useI18n} from 'vue-i18n';
import SPageBlock from '../../design-system/SPageBlock.vue';
import SPageHeader from '../../design-system/SPageHeader.vue';
import {Role} from '../../types/entities/role.js';
import {computed, reactive} from 'vue';
import SIcon from '../../design-system/SIcon.vue';
import STable from '../../design-system/STable.vue';
import SMultiSelectFilter from '../../design-system/filters/SMultiSelectFilter.vue';
import SFilterGroup from '../../design-system/SFilterGroup.vue';
import SSelectFilter from '../../design-system/filters/SSelectFilter.vue';

const {roles, scopeToRoleMappings} = defineProps<{
  roles: (Role & {permissions: string[]})[];
  scopeToRoleMappings: Record<string, string[]>;
}>();

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const filters = reactive({
  roles: [] as string[],
  permissions: [] as string[],
  scope: '',
});

const filteredRoles = computed(() => {
  let filtered = [...roles];

  if (filters.roles.length) {
    filtered = filtered.filter((role: Role) => filters.roles.includes(role.id));
  }

  if (filters.scope) {
    filtered = filtered.filter((role: Role) => getScopeForRole(role.id) === filters.scope);
  }

  return filtered;
});

const permissions = computed(() => {
  const permissions = new Set<string>();
  filteredRoles.value.forEach((role) => {
    role.permissions.forEach((permission) => {
      permissions.add(permission);
    });
  });
  return Array.from(permissions);
});

const filteredPermissions = computed(() => {
  if (!filters.permissions.length) {
    return permissions.value;
  }

  return permissions.value.filter((permission) => filters.permissions.includes(permission));
});

function getScopeForRole(roleId: string): string {
  const scope = Object.keys(scopeToRoleMappings).find((scope) =>
    scopeToRoleMappings[scope].includes(roleId)
  );

  return scope || '';
}
</script>
<i18n>
{
  "en": {
    "permissions": "Permissions",
    "scope": "Scope | Scopes",
    "permissions.view.courseAdmin": "View Course Admin",
    "permissions.create.courses": "Create Courses",
    "permissions.update.courses": "Update Courses",
    "permissions.delete.courses": "Delete Courses",
    "permissions.view.courses": "View Courses",
    "permissions.view.course.rosters": "View Course Rosters",
    "permissions.update.course.rosters": "Update Course Rosters",
    "permissions.create.course_roles": "Create Course Roles",
    "permissions.update.course_roles": "Update Course Roles",
    "permissions.delete.course_roles": "Delete Course Roles",
    "permissions.create.roll-calls": "Create Roll Calls",
    "permissions.viewAny.sections": "View Any Sections",
    "permissions.create.sections": "Create Sections",
    "permissions.update.sections": "Update Sections",
    "permissions.delete.sections": "Delete Sections",
    "permissions.view.user.information": "View User Information",
    "permissions.view.user.email_addresses": "View User Email Addresses",
    "permissions.view.user.activity_logs": "View User Activity Logs",
    "permissions.view.institutions": "View Institutions",
    "permissions.create.institutions": "Create Institutions",
    "permissions.view.event.rosters": "View Event Rosters",
    "permissions.view.events": "View Events",
    "permissions.delete.events": "Delete Events",
    "permissions.create.events": "Create Events",
    "permissions.update.events": "Update Events",
    "permissions.update.event.rosters": "Update Event Rosters",
    "permissions.view.event.schedules": "View Event Schedules",
    "permissions.create.event.schedules": "Create Event Schedules",
    "permissions.update.event.schedules": "Update Event Schedules",
    "permissions.delete.event.schedules": "Delete Event Schedules",
    "permissions.create.tasks": "Create Tasks",
    "permissions.delete.tasks": "Delete Tasks",
    "permissions.update.tasks": "Update Tasks",
    "permissions.viewAny.tasks": "View Any Tasks",
    "permissions.viewConfig.task": "View Task Config",
    "permissions.view.tasks": "View Tasks",
    "permissions.reissue.question_states": "Reissue Question States",
    "permissions.view.issued.task.states": "View Issued Task States",
    "permissions.create.assignments": "Create Assignments",
    "permissions.view.owned.assignments": "View Owned Assignments",
    "permissions.view.assigned.assignments": "View Assigned Assignments",
    "permissions.update.assignments": "Update Assignments",
    "permissions.delete.assignments": "Delete Assignments",
    "permissions.test.assignments": "Test Assignments",
    "permissions.view.course_like_assignments": "View Course-like Assignments",
    "permissions.assign.course_like_assignments": "Assign Course-like Assignments",
    "permissions.view.assignment.extensions": "View Assignment Extensions",
    "permissions.grant.assignment.extensions": "Grant Assignment Extensions",
    "permissions.delete.assignment.extensions": "Delete Assignment Extensions",
    "permissions.revoke.assignment.extensions": "Revoke Assignment Extensions",
    "permissions.view.assignment.timer_extensions": "View Assignment Timer Extensions",
    "permissions.grant.assignment.timer_extensions": "Grant Assignment Timer Extensions",
    "permissions.edit.assignment.timer_extensions": "Edit Assignment Timer Extensions",
    "permissions.delete.assignment.timer_extensions": "Delete Assignment Timer Extensions",
    "permissions.create.locations": "Create Locations",
    "permissions.view.locations": "View Locations",
    "permissions.update.locations": "Update Locations",
    "permissions.delete.locations": "Delete Locations",
    "permissions.view.grades": "View Grades",
    "permissions.submit.grades": "Submit Grades",
    "permissions.view.responses": "View Responses",
    "permissions.submit.responses": "Submit Responses",
    "permissions.try.tasks": "Try Tasks",
    "permissions.view.payment.requests": "View Payment Requests",
    "permissions.view.analytics": "View Analytics",
    "permissions.create.lti.course_context_links": "Create LTI Course Context Links",
    "permissions.delete.lti.course_context_links": "Delete LTI Course Context Links",
    "permissions.view.lti.registrations": "View LTI Registrations",
    "permissions.create.lti.registrations": "Create LTI Registrations",
    "permissions.update.lti.registrations": "Update LTI Registrations",
    "permissions.delete.lti.registrations": "Delete LTI Registrations",
    "permissions.create.task_attempts_grants": "Create Task Attempts Grants",
    "permissions.create.topic.tags": "Create Topic Tags",
    "permissions.delete.topic.tags": "Delete Topic Tags"
  },
  "fr": {
    "permissions": "Permissions",
    "scope": "Portée | Portées",
    "permissions.view.courseAdmin": "Voir l'administration des cours",
    "permissions.create.courses": "Créer des cours",
    "permissions.update.courses": "Mettre à jour les cours",
    "permissions.delete.courses": "Supprimer des cours",
    "permissions.view.courses": "Voir les cours",
    "permissions.view.course.rosters": "Voir les listes de cours",
    "permissions.update.course.rosters": "Mettre à jour les listes de cours",
    "permissions.create.course_roles": "Créer des rôles de cours",
    "permissions.update.course_roles": "Mettre à jour les rôles de cours",
    "permissions.delete.course_roles": "Supprimer des rôles de cours",
    "permissions.create.roll-calls": "Créer des appels",
    "permissions.viewAny.sections": "Voir toutes les sections",
    "permissions.create.sections": "Créer des sections",
    "permissions.update.sections": "Mettre à jour les sections",
    "permissions.delete.sections": "Supprimer des sections",
    "permissions.view.user.information": "Voir les informations utilisateur",
    "permissions.view.user.email_addresses": "Voir les adresses e-mail utilisateur",
    "permissions.view.user.activity_logs": "Voir les journaux d'activité utilisateur",
    "permissions.view.institutions": "Voir les institutions",
    "permissions.create.institutions": "Créer des institutions",
    "permissions.view.event.rosters": "Voir les listes d'événements",
    "permissions.view.events": "Voir les événements",
    "permissions.delete.events": "Supprimer des événements",
    "permissions.create.events": "Créer des événements",
    "permissions.update.events": "Mettre à jour les événements",
    "permissions.update.event.rosters": "Mettre à jour les listes d'événements",
    "permissions.view.event.schedules": "Voir les horaires d'événements",
    "permissions.create.event.schedules": "Créer des horaires d'événements",
    "permissions.update.event.schedules": "Mettre à jour les horaires d'événements",
    "permissions.delete.event.schedules": "Supprimer les horaires d'événements",
    "permissions.create.tasks": "Créer des tâches",
    "permissions.delete.tasks": "Supprimer des tâches",
    "permissions.update.tasks": "Mettre à jour les tâches",
    "permissions.viewAny.tasks": "Voir toutes les tâches",
    "permissions.viewConfig.task": "Voir la configuration des tâches",
    "permissions.view.tasks": "Voir les tâches",
    "permissions.reissue.question_states": "Réémettre les états de questions",
    "permissions.view.issued.task.states": "Voir les états de tâches émises",
    "permissions.create.assignments": "Créer des devoirs",
    "permissions.view.owned.assignments": "Voir les devoirs possédés",
    "permissions.view.assigned.assignments": "Voir les devoirs assignés",
    "permissions.update.assignments": "Mettre à jour les devoirs",
    "permissions.delete.assignments": "Supprimer des devoirs",
    "permissions.test.assignments": "Tester des devoirs",
    "permissions.view.course_like_assignments": "Voir les devoirs de type cours",
    "permissions.assign.course_like_assignments": "Assigner des devoirs de type cours",
    "permissions.view.assignment.extensions": "Voir les extensions de devoirs",
    "permissions.grant.assignment.extensions": "Accorder des extensions de devoirs",
    "permissions.delete.assignment.extensions": "Supprimer des extensions de devoirs",
    "permissions.revoke.assignment.extensions": "Révoquer des extensions de devoirs",
    "permissions.view.assignment.timer_extensions": "Voir les extensions de minuteur de devoirs",
    "permissions.grant.assignment.timer_extensions": "Accorder des extensions de minuteur de devoirs",
    "permissions.edit.assignment.timer_extensions": "Modifier les extensions de minuteur de devoirs",
    "permissions.delete.assignment.timer_extensions": "Supprimer les extensions de minuteur de devoirs",
    "permissions.create.locations": "Créer des emplacements",
    "permissions.view.locations": "Voir les emplacements",
    "permissions.update.locations": "Mettre à jour les emplacements",
    "permissions.delete.locations": "Supprimer des emplacements",
    "permissions.view.grades": "Voir les notes",
    "permissions.submit.grades": "Soumettre des notes",
    "permissions.view.responses": "Voir les réponses",
    "permissions.submit.responses": "Soumettre des réponses",
    "permissions.try.tasks": "Essayer des tâches",
    "permissions.view.payment.requests": "Voir les demandes de paiement",
    "permissions.view.analytics": "Voir les analyses",
    "permissions.create.lti.course_context_links": "Créer des liens de contexte de cours LTI",
    "permissions.delete.lti.course_context_links": "Supprimer des liens de contexte de cours LTI",
    "permissions.view.lti.registrations": "Voir les enregistrements LTI",
    "permissions.create.lti.registrations": "Créer des enregistrements LTI",
    "permissions.update.lti.registrations": "Mettre à jour les enregistrements LTI",
    "permissions.delete.lti.registrations": "Supprimer des enregistrements LTI",
    "permissions.create.task_attempts_grants": "Créer des subventions de tentatives de tâches",
    "permissions.create.topic.tags": "Créer des étiquettes de sujets",
    "permissions.delete.topic.tags": "Supprimer des étiquettes de sujets"
  }
}
</i18n>

<style scoped>
.sticky-first-column {
  th:first-child,
  td:first-child {
    @apply sticky left-0 z-50 border-r border-gray-200;
  }

  tr:nth-child(even) {
    td:first-child {
      @apply !bg-gray-50;
    }
  }

  tr:nth-child(odd) {
    td:first-child {
      @apply !bg-white;
    }
  }
}

table tr td.green-cell {
  @apply bg-green-100 odd:bg-green-150;
}

table tr:nth-child(even) td.green-cell {
  @apply bg-green-100/70 odd:bg-green-150/70;
}

.zebra-columns {
  tr:nth-child(odd) td:not(.green-cell):nth-child(odd) {
    @apply bg-gray-300/10;
  }

  tr:nth-child(even) td:not(.green-cell):nth-child(odd) {
    @apply bg-gray-300/20;
  }
}
</style>
